import { invariant } from '@mimir/common/util/invariant';
import { evalRawScript, getFresh } from './cep';
import type { AllNleActions, ICepBridgeClient } from '@mimir/primitive/premiere/cepBridgeClientType';
import { reconstitutedException } from '@mimir/common/util/exception';
import { createSourceLocationResolver } from './cepBridgeSourceLocation';
import type { SourceLocationResolver } from './cepBridgeSourceLocation';

const CEPBRIDGE_BUNDLE_PATH = '/assets/cepbridge.jsx';
const ARGS_PREVIEW_LIMIT = 500;

interface CepBridgeErrorPayload {
  message?: string;
  name?: string;
  line?: number;
  [key: string]: unknown;
}

export class CepBridgeClient implements ICepBridgeClient {
  initialized: boolean = false;
  private readonly resolveSourceLocation: SourceLocationResolver = createSourceLocationResolver(CEPBRIDGE_BUNDLE_PATH);

  async call<A extends keyof AllNleActions>(action: A, ...args: Parameters<AllNleActions[A]>): Promise<ReturnType<AllNleActions[A]>> {

    if (!this.initialized) {

      const bundle: string = await getFresh(CEPBRIDGE_BUNDLE_PATH);
      const s = bundle.lastIndexOf('[') + 1;
      const e = bundle.indexOf(']', s);
      const id = bundle.slice(s, e);
      const initScript = '__require=' + bundle + ';cepbridge=__require(' + id + '); "OK"';
      const result = await evalRawScript(initScript);
      invariant(result == 'OK', `Expected initialized cepbridge, got: ${result}`);
      this.initialized = true;
    }

    const argsJson = JSON.stringify(args);
    const callScript = 'cepbridge.call(' + JSON.stringify(action) + ',' + argsJson + ');';
    const reply = await evalRawScript(callScript);
    const obj = this.parseCallReply(reply, action);
    if (obj.error) {
      throw await this.formatError(obj.error, action, args);
    }
    return obj.result as ReturnType<AllNleActions[A]>;
  }

  private parseCallReply(reply: string, action: string): { result?: unknown; error?: CepBridgeErrorPayload } {
    try {
      return JSON.parse(reply);
    } catch (_) {
      throw new Error(`cepbridge.call('${action}') failed: ${reply}`);
    }
  }

  private async formatError(err: CepBridgeErrorPayload, action: string, args: unknown[]): Promise<Error> {
    const location = err.line == null ? undefined : await this.resolveSourceLocation(err.line);
    let locationText: string | undefined;
    if (location) {
      locationText = `${location.source}:${location.line}`;
    } else if (err.line != null) {
      locationText = `bundle line ${err.line}`;
    }
    const argsPretty = JSON.stringify(args, null, 2);
    const argsPreview = argsPretty.length > ARGS_PREVIEW_LIMIT ? `${argsPretty.slice(0, ARGS_PREVIEW_LIMIT)}…` : argsPretty;
    const exception = reconstitutedException({
      ...err,
      ...(locationText !== undefined && { location: locationText }),
    });
    if (typeof exception.stack === 'string') {
      // Drop the native header line and the reconstitutedException frame
      // right below it (new Error() is constructed inside that function,
      // one frame deeper than any of its callers) - keeping this local
      // instead of changing reconstitutedException for every other caller.
      const [, , ...rest] = exception.stack.split('\n');
      const contextLine = `ExtendScript error in ${action}(${argsPreview})${locationText === undefined ? '' : ` at ${locationText}`} ${exception.name}: ${exception.message}`;
      const frame = location ? [`    at ${action} (${location.source}:${location.line})`] : [];
      exception.stack = [contextLine, ...frame, ...rest].join('\n');
    }
    return exception;
  }
}
